import React, { useEffect, useState, createRef, useRef, RefObject } from 'react';
import cn from 'classnames';
import './styles.scss';

import { IAppState } from 'src/store';
import useMeasure from 'src/hooks/useMeasure';
import { isEmpty, isNumeric } from 'src/validations';
import { generateArray } from 'src/utils';
import { Input, View } from '../common';
import { connect } from 'react-redux';

const MAX_SIZE = 72;
const SPACE_BETWEEN = 20;
const SPACE_BETWEEN_SMALL = 8;
const SPACE_AROUND = 8;

const getDiff = (str1: string, str2: string) => {
  const res = str2.replace(str1, '');
  return res;
};

const ComfirmationCodeField: React.FC<Props> = ({ label, errorMessage, fields = 6, onChange }) => {
  const [refs, setRefs] = useState<RefObject<HTMLInputElement>[]>([]);
  const [valueArr, setValue] = useState(generateArray(fields));
  const {
    bind,
    bounds: { width },
  } = useMeasure();
  const [cellSize, setCellSize] = useState(MAX_SIZE);
  const isBackward = useRef(false);

  // Auto size width of text field when screen is small
  useEffect(() => {
    if (width > 0) {
      const spaceBetween = width > 768 ? SPACE_BETWEEN : SPACE_BETWEEN_SMALL;
      const size = (width - spaceBetween * (fields - 1) - SPACE_AROUND * 2) / fields;

      if (size < MAX_SIZE) setCellSize(size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  // Link ref to input fields
  useEffect(() => {
    setRefs(elRefs => generateArray(fields).map((_, i) => elRefs[i] || createRef()));
  }, [fields]);

  const handleInputChange = (idx: number) => (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    handleValueChange(idx, value);
  };

  const handleValueChange = (idx: number, value: string) => {
    if (isNumeric(value)) {
      const updatedValue = [...valueArr];
      const pasteValue = getDiff(valueArr[idx], value);

      if (isEmpty(pasteValue)) {
        updatedValue[idx] = '';
        move(idx);
      } else {
        pasteValue.split('').map((x, i) => (updatedValue[idx + i] = x));
        move(idx + pasteValue.length - 1);
      }

      updatedValue.splice(fields);
      setValue(updatedValue);
      onChange(updatedValue.join(''));
    }
  };

  const handleKeyPress = (idx: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    const key = event.key;
    switch (key) {
      case 'ArrowLeft':
        isBackward.current = true;
        move(idx);
        break;
      case 'ArrowRight':
        move(idx);
        break;
      case 'Delete':
        handleValueChange(idx, '');
        break;
      case 'Backspace':
        isBackward.current = true;
        handleValueChange(idx, '');
        // prevent Default to prevent delete 2 times
        event.preventDefault();
        break;
      default:
        break;
    }
  };

  const move = (idx: number) => {
    if (isBackward.current) {
      isBackward.current = false;
      moveBackward(idx);
    } else {
      moveForward(idx);
    }
  };

  const moveForward = (idx: number) => {
    if (idx + 1 < fields) {
      refs[idx + 1]?.current?.focus();
    } else {
      refs[fields - 1].current?.focus();
    }
  };

  const moveBackward = (idx: number) => {
    idx > 0 && refs[idx - 1].current?.focus();
  };

  const cellStyle = {
    width: `${cellSize}px`,
    height: `${cellSize}px`,
  };

  const hasLabel = !!label;
  const hasError = !!errorMessage;
  return (
    <View className={cn('cmp-code-field')}>
      {hasLabel && <label className={cn('cmp-code-field__label')}>{label}</label>}
      <div className={cn('cmp-code-field__container')} {...bind}>
        {generateArray(fields).map((_, index) => (
          <Input
            key={`code-field-${index}`}
            inputMode="decimal"
            inputRef={refs[index]}
            containerClassName={cn('cmp-code-field__input--container')}
            className={cn('cmp-code-field__input')}
            errorMessage={hasError ? ' ' : ''}
            value={valueArr[index]}
            onKeyDown={handleKeyPress(index)}
            onChange={handleInputChange(index)}
            style={cellStyle}
          />
        ))}
      </div>
      {hasError && <p className={cn('cmp-code-field__error')}>{errorMessage}</p>}
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps & {
    label?: string;
    errorMessage?: string;
    onChange: (...arg: any[]) => void;
    fields?: number;
  };

const mapStateToProps = (state: IAppState) => ({});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ComfirmationCodeField);
